import { useEffect } from 'react';
import { usePageContext } from '@providers/PageMetaProvider/model/PageContext';
import { GenerationProgressDashboard } from '@shared/components/GenerationProgressDashboard';
import {
  setDocumentFinalStatus,
  useGenerationProgress,
} from '@shared/hooks/useGenerationProgress';
import { getUseCaseGenerationStatus } from '@app/services/api/use-case';

function UseCaseGeneratingModal() {
  const { values, setValues } = usePageContext();

  // All three popups are always mounted and share generatingDocumentId, so
  // each one only tracks it while its own popup is open. Otherwise the demand
  // popup asked /document-instances/<report id>/progress/ for every report
  // (ZLAYT-1514).
  const documentId = values?.generateUseCaseModalOpened
    ? values?.generatingDocumentId || ''
    : '';
  const documentType = values?.generatingDocumentType || 'Report';
  const autoMinimize = values?.progressModalAutoMinimize;
  const expandedSignal = values?.progressModalExpandedAt;

  const progressData = useGenerationProgress(documentId, {
    enableSnapshot: true,
    enablePolling: true,
    enableSynthetic: true,
    pipeline: 'use_case',
  });

  useEffect(() => {
    if (
      !values?.generateUseCaseModalOpened ||
      !documentId ||
      progressData.finalStatus
    ) {
      return;
    }

    let isActive = true;
    const pollStatus = async () => {
      try {
        const data = await getUseCaseGenerationStatus(Number(documentId));
        if (!isActive || !data?.status) return;
        const normalized = String(data.status).toLowerCase();
        if (normalized === 'failed') {
          setDocumentFinalStatus(documentId, 'FAILED', data, 'use_case');
        } else if (normalized === 'completed') {
          setDocumentFinalStatus(documentId, 'COMPLETED', data, 'use_case');
        }
      } catch (error) {
        // no-op: use websocket/optimistic updates instead
      }
    };

    pollStatus();
    const interval = setInterval(pollStatus, 5000);

    return () => {
      isActive = false;
      clearInterval(interval);
    };
  }, [
    values?.generateUseCaseModalOpened,
    documentId,
    progressData.finalStatus,
  ]);

  const onClose = () => {
    if (setValues) {
      setValues((prev) => ({
        ...prev,
        generateUseCaseModalOpened: false,
        generatingDocumentId: undefined,
        generatingDocumentType: undefined,
        progressModalAutoMinimize: true,
        progressModalExpandedAt: undefined,
      }));
    }
  };

  if (!values?.generateUseCaseModalOpened || !documentId) {
    return null;
  }

  return (
    <GenerationProgressDashboard
      documentId={documentId}
      documentType={documentType}
      {...progressData}
      onClose={onClose}
      autoMinimize={autoMinimize}
      expandedSignal={expandedSignal}
      chargeReturnedOnFailure
    />
  );
}

export default UseCaseGeneratingModal;
